import { useCallback, useEffect, useState } from 'react';
import type { PublicEvent, StateView } from '@quiz/shared';
import { api } from '../lib/api.js';
import { usePoll } from '../lib/usePoll.js';
import {
  clearSession,
  loadSession,
  loadCodes,
  saveSession,
  type PlayerSession,
} from '../lib/session.js';
import { takeReadySignal, clearReadySignal } from '../lib/readySignal.js';
import { Screen, Spinner, Banner, Button } from '../components/ui.js';
import { JoinScreen } from './JoinScreen.js';
import { LobbyScreen } from './LobbyScreen.js';
import { QuestionScreen } from './QuestionScreen.js';
import { RevealScreen } from './RevealScreen.js';
import { BreakScreen } from './BreakScreen.js';
import { EndScreen } from './EndScreen.js';
import { RemovedScreen } from './RemovedScreen.js';
import { HomeScreen } from './HomeScreen.js';

export function PlayerApp() {
  const [session, setSession] = useState<PlayerSession | null>(loadSession);
  const [leaving, setLeaving] = useState(false);
  /**
   * Which event they have chosen from the home page. Null means "show me what is
   * on" -- so the home page is the front door, and the quiz is somewhere you go.
   */
  const [chosen, setChosen] = useState<PublicEvent | null>(null);

  /**
   * Deep links skip the home page.
   *
   * ?code= is what the card-payment screen hands back and what a QR with a code
   * baked in uses; ?event= links straight to one quiz. Both existed before the
   * home page did, and dropping somebody on a list when they followed a link to
   * a specific thing would be a step backwards.
   */
  const [deepLink, setDeepLink] = useState(() => {
    const params = new URLSearchParams(location.search);
    return { code: params.get('code'), eventId: params.get('event') };
  });

  useEffect(() => {
    if (!deepLink.eventId || chosen) return;
    api<PublicEvent>(`/api/events/${deepLink.eventId}`)
      .then(setChosen)
      // An unknown id falls back to the home page rather than a dead end.
      .catch(() => setDeepLink((d) => ({ ...d, eventId: null })));
  }, [deepLink.eventId, chosen]);

  const { state, error, fatal, offsetMs, refresh } = usePoll<StateView>(
    '/api/state',
    session?.playerToken ?? null,
    2000,
    // Rides on the poll that is already happening. Sent once per question, not
    // once per poll -- see lib/readySignal.ts.
    useCallback(() => {
      const q = takeReadySignal();
      return q ? `ready=${encodeURIComponent(q)}` : null;
    }, []),
  );

  /**
   * Re-fetch the sealed quiz when this phone's copy has gone stale.
   *
   * The quiz is downloaded once, at join, and never looked at again -- that is
   * what makes a round survive a wifi blip. The cost is that a question which
   * gained a picture AFTER somebody joined leaves them holding a copy that does
   * not know about it, silently, for the rest of the night: no download, no
   * media on screen, and a readiness tally that never explains itself.
   *
   * The poll says whether the current question has a file; the cached copy says
   * what this phone believes. When they disagree, this phone is wrong.
   */
  useEffect(() => {
    const q = state?.question;
    if (!session || !q) return;
    const cached = session.quiz.rounds
      .flatMap((r) => r.questions)
      .find((x) => x.id === q.id);
    if (!cached || Boolean(cached.media) === q.hasMedia) return;

    let cancelled = false;
    void api<{ quiz: PlayerSession['quiz'] }>('/api/me/quiz', { token: session.playerToken })
      .then(({ quiz }) => {
        if (cancelled) return;
        const next = { ...session, quiz };
        saveSession(next);
        setSession(next);
      })
      .catch(() => {
        // Next poll tries again. A stale copy still plays the question; it just
        // misses the picture, which is better than a blank screen.
      });
    return () => {
      cancelled = true;
    };
  }, [state?.question, session]);

  const onAnswered = useCallback(() => refresh(), [refresh]);

  const reset = useCallback(() => {
    clearSession();
    clearReadySignal();
    setSession(null);
    setChosen(null);
    setLeaving(false);
  }, []);

  /**
   * A 401 means the server has never heard of this session -- a token left in
   * localStorage from a previous event, usually. There is nothing to tell the
   * player and nothing they can do about it, so bin it and put the join screen
   * back rather than stranding them on a dead end they have to tap out of.
   *
   * A 403 (PLAYER_REMOVED) is different: that one they need to be told about.
   */
  useEffect(() => {
    if (fatal?.status === 401) reset();
  }, [fatal, reset]);

  const goHome = () => {
    // Drop the deep-link params, or "back to all quizzes" bounces straight in again.
    if (location.search) history.replaceState(null, '', location.pathname);
    setDeepLink({ code: null, eventId: null });
    setChosen(null);
  };

  // Not in a quiz, not on the way into one, and not deep-linked: show what is on.
  if (!session && !chosen && !deepLink.code && !deepLink.eventId) {
    return <HomeScreen onPlay={setChosen} activeEventId={null} callSign={null} />;
  }

  if (!session) {
    return (
      <JoinScreen
        event={chosen}
        savedCode={chosen ? (loadCodes()[chosen.id] ?? null) : null}
        onJoined={setSession}
        onBack={goHome}
      />
    );
  }

  // Ejected. Polling has already stopped; say what happened rather than spinning.
  if (fatal) return <RemovedScreen fatal={fatal} onReset={reset} />;

  if (!state) {
    return (
      <Screen>
        <Spinner label="Connecting" />
        {error && (
          <div className="pb-4">
            <Banner kind="error">
              <span className="block">{error}</span>
              <button
                onClick={reset}
                className="mt-3 rounded-lg bg-ink-2 px-4 py-2 text-sm font-semibold hover:bg-ink-3"
              >
                Start again
              </button>
            </Banner>
          </div>
        )}
      </Screen>
    );
  }

  return (
    <Screen>
      <header className="mb-4 flex items-center justify-between text-sm">
        {/* Tapping your own name is the way out. Deliberately not a visible
            "sign out" button: on a paid night leaving is close to irreversible,
            because the ticket code has already been spent. */}
        <button
          onClick={() => setLeaving(true)}
          className="font-semibold underline decoration-ink-3 underline-offset-4"
        >
          {session.callSign}
        </button>
        <span className="text-muted">
          {/* "Waiting" in the holding area, "playing" once it has started --
              the count means different things either side of the host's cue. */}
          {state.playerCount} {state.phase === 'LOBBY' ? 'waiting' : 'playing'}
          {state.phase !== 'LOBBY' && ` · ${state.you.score} pts`}
        </span>
      </header>

      {/* A blip is not an event: the questions are already on the phone, so we
          say so quietly at the bottom and let the next poll fix it. */}
      {error && (
        <div className="mb-3">
          <Banner kind="info">Reconnecting…</Banner>
        </div>
      )}

      {leaving && (
        <LeaveConfirm
          callSign={session.callSign}
          joinMode={state.joinMode}
          ended={state.phase === 'ENDED'}
          onCancel={() => setLeaving(false)}
          onLeave={reset}
        />
      )}

      {state.phase === 'LOBBY' && <LobbyScreen session={session} state={state} />}

      {(state.phase === 'OPEN' || state.phase === 'CLOSED') && !state.reveal && (
        state.question ? (
          <QuestionScreen
            session={session}
            state={state}
            offsetMs={offsetMs}
            onAnswered={onAnswered}
          />
        ) : (
          <Staged state={state} />
        )
      )}

      {state.phase === 'REVEALED' && <RevealScreen state={state} playerId={session.playerId} />}
      {state.phase === 'ROUND_BREAK' && <BreakScreen session={session} state={state} />}
      {state.phase === 'ENDED' && (
        <EndScreen session={session} state={state} onLeave={() => setLeaving(true)} />
      )}
    </Screen>
  );
}

/** The question is staged but not open: the host is reading it out. */
function Staged({ state }: { state: StateView }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <p className="text-xs uppercase tracking-wide text-muted">{state.round?.title}</p>
      <p className="text-2xl font-semibold">Listen up…</p>
      <p className="text-muted">The host is about to start the clock.</p>
    </div>
  );
}

/**
 * Leaving is cheap on the practice quiz and expensive on a paid night, so this
 * says which one you are on rather than asking "are you sure?" and hoping.
 *
 * Once the quiz has ENDED it is cheap on every night, and the warnings invert:
 * there is no seat left to lose and nothing to rejoin, so telling somebody their
 * ticket is spent would be alarming and untrue. What it does cost is this
 * scoreboard -- the summary is fetched with the player token, and the event has
 * dropped off the home page -- so that is what it says instead.
 */
function LeaveConfirm({
  callSign,
  joinMode,
  ended,
  onCancel,
  onLeave,
}: {
  callSign: string;
  joinMode: StateView['joinMode'];
  ended: boolean;
  onCancel: () => void;
  onLeave: () => void;
}) {
  const ticketed = joinMode === 'TICKET' && !ended;

  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center bg-ink/80 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-ink-2 p-5">
        <p className="text-xs uppercase tracking-wide text-muted">Signed in as</p>
        <p className="mt-1 text-2xl font-bold">{callSign}</p>

        <div className={`mt-4 rounded-xl p-3 text-sm ${ticketed ? 'bg-stop/15 text-stop' : 'bg-ink p-3 text-muted'}`}>
          {ended
            ? 'This is the last look at your result: the standings and your breakdown close with it. Any codes saved on this phone are kept.'
            : ticketed
              ? 'Your ticket code has already been used, so leaving may mean you cannot get back in. Ask the staff if you need to rejoin.'
              : 'You can rejoin, but you will need a different name — this one stays taken for the rest of the night.'}
        </div>

        <p className="mt-3 text-sm text-muted">
          Your score stays on the leaderboard either way.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button variant="ghost" onClick={onCancel}>
            {ended ? 'Not yet' : 'Stay in'}
          </Button>
          <Button variant={ended ? 'primary' : 'danger'} onClick={onLeave}>
            {ended ? 'Back to the list' : 'Leave quiz'}
          </Button>
        </div>
      </div>
    </div>
  );
}
